import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import api from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, Save, Eraser, Send, ListChecks, Mail, Eye, X, FileText, CircleAlert, CircleCheck, CircleSlash, Clock as ClockIcon } from "lucide-react";


/**
 * Friendly relative time for "last delivery" badges. Returns null if iso is falsy.
 */
function _fmtRelative(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1)   return "just now";
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30)  return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
}


/**
 * Render the per-template "Last delivery" badge using the new stats fields
 * returned by GET /admin/email/templates (v38).
 *
 * Three visible states + the never-fired baseline:
 *   • SENT      — green pill with relative "Nm ago" (template is healthy)
 *   • SKIPPED   — amber pill with the skip reason (template fired but a
 *                 gate said no: master switch off, per-template toggle off,
 *                 no API key, no recipients, etc.). This is the silent-
 *                 failure mode the user got bit by — now LOUD.
 *   • ERROR     — rose pill with the Resend error message
 *   • NEVER     — neutral grey hint "never fired" (default state for new templates)
 */
function LastDeliveryBadge({ row }) {
  const lastSent     = row.last_sent_at;
  const lastAttempt  = row.last_attempt_at;
  const lastStatus   = row.last_attempt_status;
  const lastReason   = row.last_skip_reason;
  if (!lastAttempt) {
    return (
      <Badge variant="outline"
             className="text-[10px] bg-slate-50 text-slate-500 border-slate-200 gap-1 font-medium"
             data-testid={`tpl-delivery-${row.key}`}>
        <ClockIcon className="w-3 h-3" /> Never fired
      </Badge>
    );
  }
  if (lastStatus === "sent") {
    return (
      <Badge variant="outline"
             className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 gap-1 font-medium"
             title={`Last sent ${lastSent ? new Date(lastSent).toLocaleString() : "—"}`}
             data-testid={`tpl-delivery-${row.key}`}>
        <CircleCheck className="w-3 h-3" /> Sent {_fmtRelative(lastSent || lastAttempt)}
      </Badge>
    );
  }
  if (lastStatus === "skipped") {
    return (
      <Badge variant="outline"
             className="text-[10px] bg-amber-50 text-amber-800 border-amber-200 gap-1 font-medium max-w-[220px] truncate"
             title={`Skipped ${_fmtRelative(lastAttempt)} — reason: ${lastReason || "unknown"}`}
             data-testid={`tpl-delivery-${row.key}`}>
        <CircleSlash className="w-3 h-3" /> Skipped: {lastReason || "(no reason)"}
      </Badge>
    );
  }
  // error
  return (
    <Badge variant="outline"
           className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 gap-1 font-medium max-w-[220px] truncate"
           title={`Error ${_fmtRelative(lastAttempt)} — ${lastReason || "unknown"}`}
           data-testid={`tpl-delivery-${row.key}`}>
      <CircleAlert className="w-3 h-3" /> Error: {lastReason || "send failed"}
    </Badge>
  );
}

/**
 * /admin/email — Resend back-office.
 *
 * Two channels with **independent** flags:
 *   • Ops    — internal staff alerts (kyc.verified / kyc.failed). Safe to switch ON immediately.
 *   • Customer — MSB-regulated customer emails. Stays OFF until content + unsubscribe review.
 *
 * Mirrors AdminKycaid.jsx UX: masked key, source labels, edit form, "clear overrides".
 */
export default function AdminEmail() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({ api_key: "", sender_email: "", ops_recipients: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [log, setLog] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);   // { key, subject, html, ... }
  const [tplTestKey, setTplTestKey] = useState(null);
  const [tplTestTo, setTplTestTo] = useState("");
  const [tplTesting, setTplTesting] = useState(false);

  const loadCfg = async () => {
    try { const r = await api.get("/admin/email/config"); setCfg(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load email config"); }
  };
  const loadLog = async () => {
    try { const r = await api.get("/admin/email/log?limit=20"); setLog(r.data?.rows || []); }
    catch (e) { /* silent */ }
  };
  const loadTemplates = async () => {
    try { const r = await api.get("/admin/email/templates"); setTemplates(r.data?.rows || []); }
    catch (e) { /* silent */ }
  };

  /* eslint-disable */
  useEffect(() => { loadCfg(); loadLog(); loadTemplates(); }, []);
  /* eslint-enable */

  const flipTemplate = async (key, value) => {
    // Optimistic update
    setTemplates((rows) => rows.map((r) => r.key === key ? { ...r, enabled: value, source: "settings" } : r));
    try {
      await api.patch(`/admin/email/templates/${key}`, { enabled: value });
      toast.success(`${key}: ${value ? "enabled" : "disabled"}`);
    } catch (e) {
      // Revert
      toast.error(e?.response?.data?.detail || "Failed to flip template");
      loadTemplates();
    }
  };

  const openPreview = async (key) => {
    try {
      const r = await api.get(`/admin/email/templates/${key}/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const sendTemplateTest = async () => {
    if (!tplTestKey || !tplTestTo.trim() || !tplTestTo.includes("@")) {
      toast.error("Enter a valid recipient"); return;
    }
    setTplTesting(true);
    try {
      const r = await api.post(`/admin/email/templates/${tplTestKey}/test`, { to: tplTestTo.trim() });
      if (r.data?.ok) {
        toast.success(`Sent (provider id: ${r.data.provider_id || "—"})`);
      } else {
        toast.warning(`Skipped — ${r.data?.reason || "unknown"}`);
      }
      setTplTestKey(null); setTplTestTo("");
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setTplTesting(false); }
  };

  const flipFlag = async (flag, value) => {
    try {
      const r = await api.patch("/admin/email/config", { [flag]: value });
      setCfg(r.data);
      toast.success(`${flag} = ${value}`);
    } catch (e) { toast.error("Failed to update flag"); }
  };

  const saveCfg = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (draft.api_key !== "")        patch.api_key = draft.api_key;
      if (draft.sender_email !== "")   patch.sender_email = draft.sender_email;
      if (draft.ops_recipients !== "") patch.ops_recipients = draft.ops_recipients;
      const r = await api.patch("/admin/email/config", patch);
      setCfg(r.data);
      setEditOpen(false);
      setDraft({ api_key: "", sender_email: "", ops_recipients: "" });
      toast.success("Email config updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update config");
    } finally { setSaving(false); }
  };

  const clearCfg = async () => {
    if (!window.confirm("Clear Resend overrides? Falls back to env vars.")) return;
    try {
      const r = await api.patch("/admin/email/config", {
        api_key: "", sender_email: "", ops_recipients: "",
      });
      setCfg(r.data);
      toast.success("Overrides cleared.");
    } catch (e) { toast.error("Failed to clear"); }
  };

  const sendTest = async () => {
    if (!testTo.trim() || !testTo.includes("@")) { toast.error("Enter a valid recipient"); return; }
    setTesting(true);
    try {
      const r = await api.post("/admin/email/test", { to: testTo.trim() });
      if (r.data?.ok) {
        toast.success(`Sent (provider id: ${r.data.provider_id || "—"})`);
      } else {
        toast.warning(`Skipped — ${r.data?.reason || "unknown"}`);
      }
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setTesting(false); }
  };

  if (!cfg) {
    return <AdminLayout title="Email"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;
  }

  return (
    <AdminLayout
      title="Email & ops notifications"
      subtitle="Resend integration with split flags. Ops alerts (KYCAID, etc.) and customer-facing MSB comms are gated independently."
    >
      {/* Config card */}
      <div className="xch-card p-5 mb-5" data-testid="email-config-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 border ${cfg.api_key_set ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            <KeyRound className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-foreground">Resend API connection</div>
              <Badge variant="outline"
                     className={`text-[10px] uppercase tracking-wider font-bold ${cfg.api_key_set ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                     data-testid="email-config-key-badge">
                {cfg.api_key_set ? "Key set" : "Not wired"}
              </Badge>
              {!cfg.sdk_installed && (
                <Badge variant="outline" className="text-[10px] uppercase font-bold bg-rose-50 text-rose-700 border-rose-200">
                  SDK missing
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 mt-2 text-xs">
              <div className="text-muted-foreground">API key</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2" data-testid="email-config-key-mask">
                {cfg.api_key_set ? cfg.api_key_mask : <em className="text-muted-foreground">not set</em>}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.api_key})</span>
              </div>
              <div className="text-muted-foreground">From</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2">
                {cfg.sender_email}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.sender_email})</span>
              </div>
              <div className="text-muted-foreground">Ops recipients</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2" data-testid="email-config-ops-recipients">
                {(cfg.ops_recipients || []).join(", ") || <em className="text-muted-foreground">none</em>}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.ops_recipients})</span>
              </div>
            </div>
            {!editOpen && (
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button onClick={() => setEditOpen(true)} variant="outline" size="sm"
                        className="rounded-xl gap-1.5" data-testid="email-config-edit-btn">
                  <KeyRound className="w-3.5 h-3.5" /> Rotate / set creds
                </Button>
                {(cfg.source.api_key === "settings" || cfg.source.sender_email === "settings" || cfg.source.ops_recipients === "settings") && (
                  <Button onClick={clearCfg} variant="outline" size="sm"
                          className="rounded-xl gap-1.5" data-testid="email-config-clear-btn">
                    <Eraser className="w-3.5 h-3.5" /> Clear overrides
                  </Button>
                )}
              </div>
            )}
            {editOpen && (
              <div className="mt-3 space-y-2.5 rounded-xl border border-border bg-muted/30 p-3" data-testid="email-config-edit-form">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Resend API key</Label>
                  <Input type="password" value={draft.api_key}
                         onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
                         placeholder="re_… (paste new key)"
                         className="rounded-xl mt-1" data-testid="email-config-key-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Sender email (must be verified)</Label>
                  <Input value={draft.sender_email}
                         onChange={(e) => setDraft({ ...draft, sender_email: e.target.value })}
                         placeholder="alerts@xchangely.io"
                         className="rounded-xl mt-1" data-testid="email-config-sender-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Ops recipients (comma-separated)</Label>
                  <Input value={draft.ops_recipients}
                         onChange={(e) => setDraft({ ...draft, ops_recipients: e.target.value })}
                         placeholder="ops@xchangely.io, compliance@xchangely.io"
                         className="rounded-xl mt-1" data-testid="email-config-recipients-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button onClick={() => { setEditOpen(false); setDraft({ api_key: "", sender_email: "", ops_recipients: "" }); }}
                          variant="ghost" size="sm" className="rounded-xl">Cancel</Button>
                  <Button onClick={saveCfg} disabled={saving || (!draft.api_key && !draft.sender_email && !draft.ops_recipients)}
                          className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                          data-testid="email-config-save-btn">
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Leave blank to keep current value. Changes take effect immediately — no restart.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Split flags */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="xch-card p-4" data-testid="ops-flag-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-display font-bold text-foreground">Ops alerts</div>
              <p className="text-xs text-muted-foreground mt-1">
                Internal staff emails on KYCAID transitions, etc. Safe to enable the moment the Resend key + sender are verified.
              </p>
              <Badge variant="outline" className="mt-2 text-[10px] uppercase font-bold bg-emerald-50 text-emerald-700 border-emerald-200">
                Internal — no MSB content review needed
              </Badge>
            </div>
            <Switch checked={cfg.ops_enabled}
                    onCheckedChange={(v) => flipFlag("ops_enabled", v)}
                    data-testid="ops-enabled-switch" />
          </div>
        </div>
        <div className="xch-card p-4" data-testid="customer-flag-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-display font-bold text-foreground">Customer emails</div>
              <p className="text-xs text-muted-foreground mt-1">
                Order confirmations, KYC results, refund notices, SoF outcomes — MSB-regulated comms.
                Live since compliance sign-off. Per-template toggles below let you stage individual
                emails on or off without touching this master switch.
              </p>
              <Badge
                variant="outline"
                className={`mt-2 text-[10px] uppercase font-bold ${
                  cfg.customer_enabled
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}
                data-testid="customer-flag-badge">
                {cfg.customer_enabled ? "Live — MSB comms approved" : "Paused — flip on to resume"}
              </Badge>
            </div>
            <Switch checked={cfg.customer_enabled}
                    onCheckedChange={(v) => flipFlag("customer_enabled", v)}
                    data-testid="customer-enabled-switch" />
          </div>
        </div>
      </div>

      {/* Templates registry */}
      <div className="xch-card overflow-hidden mb-5" data-testid="email-templates-card">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <div>
            <div className="font-display font-bold text-foreground flex items-center gap-1.5">
              <FileText className="w-4 h-4" /> Email templates
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Per-template toggles stack <em>on top of</em> the channel flags above. Both must be ON for an email to actually send.
            </p>
          </div>
          <Button onClick={loadTemplates} variant="outline" size="sm" className="rounded-xl">Refresh</Button>
        </div>
        {templates.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading templates…</div>
        ) : (
          <div className="divide-y divide-border">
            {templates.map((t) => (
              <div key={t.key} className="p-3.5 flex items-center gap-3 flex-wrap"
                   data-testid={`tpl-row-${t.key}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-foreground">{t.label}</span>
                    <Badge variant="outline" className={`text-[9px] uppercase font-bold ${t.channel === "ops" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-indigo-50 text-indigo-700 border-indigo-200"}`}>
                      {t.channel}
                    </Badge>
                    <code className="text-[10px] text-muted-foreground font-mono">{t.key}</code>
                    {t.source === "settings" && (
                      <Badge variant="outline" className="text-[9px] uppercase font-bold bg-amber-50 text-amber-700 border-amber-200">
                        override
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{t.description}</p>
                  <div className="mt-1.5">
                    <LastDeliveryBadge row={t} />
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button onClick={() => openPreview(t.key)} variant="outline" size="sm"
                          className="rounded-xl gap-1.5 h-8"
                          data-testid={`tpl-preview-${t.key}`}>
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </Button>
                  <Button onClick={() => { setTplTestKey(t.key); setTplTestTo(""); }}
                          variant="outline" size="sm" className="rounded-xl gap-1.5 h-8"
                          data-testid={`tpl-test-${t.key}`}>
                    <Send className="w-3.5 h-3.5" /> Send test
                  </Button>
                  <Switch checked={t.enabled}
                          onCheckedChange={(v) => flipTemplate(t.key, v)}
                          data-testid={`tpl-switch-${t.key}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setPreview(null)} data-testid="tpl-preview-modal">
          <div className="bg-background rounded-2xl shadow-xl max-w-[640px] w-full max-h-[88vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-foreground text-sm">{preview.label || preview.key}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Subject: <span className="text-foreground">{preview.subject}</span></div>
              </div>
              <Button onClick={() => setPreview(null)} variant="ghost" size="icon" className="rounded-full">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto bg-[#F7F7F4] p-2">
              <iframe srcDoc={preview.html} title="email-preview"
                      sandbox=""
                      className="w-full h-[60vh] bg-white rounded-xl shadow-sm border-0"
                      data-testid="tpl-preview-iframe" />
            </div>
          </div>
        </div>
      )}

      {/* Send-test modal */}
      {tplTestKey && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setTplTestKey(null)} data-testid="tpl-test-modal">
          <div className="bg-background rounded-2xl shadow-xl max-w-[420px] w-full p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-foreground text-sm mb-1">Send test email</div>
            <div className="text-xs text-muted-foreground mb-3">
              Renders <code className="text-foreground">{tplTestKey}</code> with its sample context and sends it to the address below. Bypasses the per-template toggle (for QA) but honours the channel flag.
            </div>
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Recipient</Label>
            <Input value={tplTestTo} onChange={(e) => setTplTestTo(e.target.value)}
                   placeholder="you@example.com"
                   className="rounded-xl mt-1 mb-3"
                   data-testid="tpl-test-to-input" />
            <div className="flex gap-2 justify-end">
              <Button onClick={() => setTplTestKey(null)} variant="ghost" size="sm" className="rounded-xl">Cancel</Button>
              <Button onClick={sendTemplateTest} disabled={tplTesting || !tplTestTo}
                      className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                      data-testid="tpl-test-send-btn">
                <Send className="w-3.5 h-3.5" /> {tplTesting ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Test send */}
      <div className="xch-card p-4 mb-5" data-testid="email-test-card">
        <div className="font-display font-bold text-foreground mb-1.5 flex items-center gap-1.5">
          <Send className="w-4 h-4" /> Send a probe email
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Verifies the Resend wiring. Honours the ops flag — turn ops ON first if currently disabled. Bypasses the customer flag (it&apos;s a staff-initiated test).
        </p>
        <div className="flex gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                 placeholder="probe@example.com"
                 className="rounded-xl" data-testid="email-test-to-input" />
          <Button onClick={sendTest} disabled={testing || !testTo}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="email-test-send-btn">
            <Send className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>

      {/* Recent email log */}
      <div className="xch-card overflow-hidden" data-testid="email-log-card">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <div className="font-display font-bold text-foreground flex items-center gap-1.5">
            <ListChecks className="w-4 h-4" /> Recent email events
          </div>
          <Button onClick={loadLog} variant="outline" size="sm" className="rounded-xl gap-1.5">
            Refresh
          </Button>
        </div>
        {log.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No email events yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {log.map((row) => (
              <div key={row.id} className="p-3 flex items-center gap-3" data-testid={`email-log-row-${row.id}`}>
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase font-bold">{row.category}</Badge>
                    <Badge variant="outline"
                           className={`text-[10px] uppercase font-bold ${row.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : row.status === "skipped" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                      {row.status}{row.reason ? ` · ${row.reason}` : ""}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {(row.to || []).join(", ")} · {row.subject || "(no subject)"}
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                  {new Date(row.ts).toLocaleString("en-IE")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
